import React from 'react';
import {
    LocalShipping,
    Construction,
    CardMembership,
    Forest
} from '@mui/icons-material';
import {
    Alert,
    Button,
    Grid,
    Typography
} from '@mui/material';
import { Popup } from '../CommonComponents';
import { MissingRequirementsType } from '../../types';
import { selectUserProfile } from '../../redux/actions/users';

import { useSelector } from '../../redux/hooks';

type PropsType = {
    open: boolean;
    missingRequirements: MissingRequirementsType;
    handleClose: () => void;
    handleSenderIsQualified: () => void;
    handleSenderIsNotQualified: () => void;
};

export default function OfferPopupTenantNotQualified({
    open,
    handleClose,
    missingRequirements,
    handleSenderIsQualified,
    handleSenderIsNotQualified
}: PropsType) {
    const userProfile = useSelector(selectUserProfile());

    const missingCount = missingRequirements.missingVehicleTypes.length
        + missingRequirements.missingEquipment.length
        + missingRequirements.missingSustainability.length
        + missingRequirements.missingLicencesCertificates.length
        + missingRequirements.missingCustomLicencesCertificates.length;

    const paddingCount = missingCount % 3 === 0 ? 0 : 3 - (missingCount % 3);
    const paddingSlots = React.useMemo(() => ['pad-1', 'pad-2'], []);

    return (
        <Popup
            open={open}
            title="Uppdragskrav"
            handleClose={handleClose}
            cancelLabel="Stäng"
        >
            {missingRequirements.missingFairTransport && (
                <Alert
                    severity="warning"
                    sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                    }}
                >
                    Uppdraget kräver certifiering av Fair Transport. Detta är inget du kan ändra själv, utan utgår från verifierad data. Är du certifierad - kontakta då vår support så hjälper vi dig.
                </Alert>
            )}
            <Alert
                severity="info"
                sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1
                }}
            >
                {`Uppdraget kräver ${missingRequirements.missingFairTransport
                    ? ' dessutom '
                    : ''}
                    ${missingCount === 1 ? 'en resurs' : `${missingCount} resurser`}
                    som ni inte har angett i er företagsprofil`}
                {`${!userProfile?.isAdmin
                    ? '. Om detta inte stämmer, kontakta din administratör för att uppdatera företagets profil.'
                    : ':'}`}
            </Alert>
            <Grid
                container
                sx={{
                    px: 3,
                    pt: 3,
                    pb: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    mb: 1
                }}
            >
                <Grid item xs={12}>
                    <Grid
                        container
                        rowSpacing={1}
                        columnSpacing={1}
                        justifyContent="space-evenly"
                        alignItems="flex-start"
                    >
                        {missingRequirements.missingVehicleTypes.map((veh: string) => (
                            <Grid
                                item
                                xs={4}
                                key={veh}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'center',
                                    alignItems: 'stretch'
                                }}
                            >
                                <LocalShipping fontSize="small" color="inherit" />
                                <Typography variant="body1" sx={{ ml: 0.5, mt: -0.2 }}>
                                    {veh}
                                </Typography>
                            </Grid>
                        ))}

                        {missingRequirements.missingEquipment.map((eq: string) => (
                            <Grid
                                item
                                xs={4}
                                key={eq}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'center',
                                    alignItems: 'stretch'
                                }}
                            >
                                <Construction fontSize="small" color="inherit" />
                                <Typography variant="body1" sx={{ ml: 0.5, mt: -0.2 }}>
                                    {eq}
                                </Typography>
                            </Grid>
                        ))}

                        {missingRequirements.missingSustainability.map((sus: string) => (
                            <Grid
                                item
                                xs={4}
                                key={sus}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'center',
                                    alignItems: 'stretch'
                                }}
                            >
                                <Forest fontSize="small" color="inherit" />
                                <Typography variant="body1" sx={{ ml: 0.5, mt: -0.2 }}>
                                    {sus}
                                </Typography>
                            </Grid>
                        ))}

                        {[
                            ...missingRequirements.missingLicencesCertificates,
                            ...missingRequirements.missingCustomLicencesCertificates
                        ].map((lic: string) => (
                            <Grid
                                item
                                xs={4}
                                key={lic}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'center',
                                    alignItems: 'stretch',
                                }}
                            >
                                <CardMembership fontSize="small" color="inherit" />
                                <Typography variant="body1" sx={{ ml: 0.5, mt: -0.2 }}>
                                    {lic}
                                </Typography>
                            </Grid>
                        ))}

                        {paddingSlots.slice(0, paddingCount).map((slotId) => (
                            <Grid item xs={4} key={slotId} />
                        ))}
                    </Grid>
                </Grid>
            </Grid>
            <Grid container textAlign="center">
                {userProfile?.isAdmin && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h6" mt={3} mb={1} px={10}>
                                {`Uppfyller ditt företag ${missingCount === 1
                                    ? 'detta krav?'
                                    : `dessa ${missingCount} krav?`}`}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} mt={1}>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={() => handleSenderIsQualified()}
                            >
                                Ja, skicka prisförslag
                            </Button>
                        </Grid>
                    </>
                )}
                <Grid item xs={userProfile?.isAdmin ? 6 : 12} mt={userProfile?.isAdmin ? 1 : 3}>
                    <Button
                        color="primary"
                        variant={userProfile?.isAdmin ? 'outlined' : 'contained'}
                        onClick={() => handleSenderIsNotQualified()}
                    >
                        {userProfile?.isAdmin ? 'Nej, skicka prisförslag ändå' : 'Skicka prisförslag ändå'}
                    </Button>
                </Grid>
            </Grid>
        </Popup>
    );
}
